/**
 * apps/web :: components/whatsnext :: PartnerOffersSection
 *
 * Section 4 of the post-pass journey (ROADS-431).
 *
 * Placeholder section that hides itself entirely when no partners are
 * live. ROADS-432 (partner program) hasn't shipped yet — this component
 * renders `null` cleanly so the journey arc skips this card without an
 * empty box artefact.
 *
 * Once ROADS-432 ships, replace `getActivePartners()` with the real
 * partner-fetch hook and render a list. Until then the only way this
 * card appears in the DOM is if a future ticket flips the constant.
 *
 * Status-agnostic per ROADS-436.
 */
import { useTranslation } from "react-i18next";

/**
 * Placeholder partner roster. ROADS-432 will replace this with
 * `usePartnerOffers()` reading from the partner catalog. Keep the type
 * shape stable so the future hook can drop in without component churn.
 */
export interface Partner {
  readonly id: string;
  /** Display name. */
  readonly name: string;
  /** Short pitch (already localised by the hook layer). */
  readonly tagline: string;
  /** External landing page (affiliate code resolved upstream). */
  readonly applyUrl: string;
}

/**
 * Until ROADS-432 lands, this returns an empty array and the section
 * renders nothing. Tests can override this hook by mocking the module.
 */
export function getActivePartners(): readonly Partner[] {
  return [];
}

interface PartnerOffersSectionProps {
  /** Optional override for tests. Defaults to `getActivePartners()`. */
  readonly partners?: readonly Partner[];
}

export default function PartnerOffersSection({
  partners = getActivePartners(),
}: PartnerOffersSectionProps) {
  const { t } = useTranslation();

  // ROADS-431 hard-rule: when no partners are live, render NOTHING.
  // The section disappears entirely — no header, no empty card.
  if (partners.length === 0) {
    return null;
  }

  return (
    <section
      data-testid="whatsnext-section-partners"
      aria-labelledby="whatsnext-partners-heading"
      className="rounded-lg border border-neutral-200 bg-white p-md sm:p-lg"
    >
      <h2
        id="whatsnext-partners-heading"
        className="font-display text-xl font-bold text-brand-primary"
      >
        {t("whatsnext.partners.heading")}
      </h2>
      <p className="mt-sm text-neutral-700">{t("whatsnext.partners.intro")}</p>
      <ul className="mt-md space-y-sm" data-testid="whatsnext-partners-list">
        {partners.map((p) => (
          <li
            key={p.id}
            className="rounded-md border border-neutral-200 bg-slate-50 p-sm"
          >
            <h3 className="font-semibold text-brand-primary">{p.name}</h3>
            <p className="mt-xs text-sm text-neutral-700">{p.tagline}</p>
            <a
              href={p.applyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-sm inline-flex items-center gap-2 text-sm font-semibold text-brand-primary underline underline-offset-2 hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
            >
              {t("whatsnext.partners.applyLink")} →
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
